<template>
    <div
        class="video-player"
        :class="classes"
        ref="container"
    >
        <component
            :is="player.component"
            v-bind="player.attributes"
            class="video-player__player"
            ref="player"
            @ready="onReady"
            @play="onPlay"
            @pause="onPause"
            @timeupdate="onTimeupdate"
            @volumechange="onVolumechange"
            @end="onEnd"
        />
        <div class="video-player__controls">
            <div
                @click="play"
                class="video-player__controls__play"
            >
                <IconRenderer
                    name="Play"
                    class="video-player__controls__play__icon"
                />
            </div>
            <div class="video-player__controls__bar">
                <div class="video-player__controls__location">
                    <div class="video-player__controls__slider video-player__controls__slider--location">
                        <div
                            :style="{width: `${location}%`}"
                            class="video-player__controls__slider__complete"
                        ></div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            :value="location"
                            @input="seek($event.target.value)"
                            class="video-player__controls__slider__input"
                        >
                    </div>
                </div>
                <div class="video-player__controls__dashboard">
                    <div
                        @click="togglePlayState"
                        class="video-player__controls__play-pause"
                    >
                        <IconRenderer
                            v-if="playing"
                            name="Pause"
                            class="video-player__controls__play-pause__icon video-player__controls__play-pause__icon--pause"
                        />
                        <IconRenderer
                            v-else
                            name="Play"
                            class="video-player__controls__play-pause__icon video-player__controls__play-pause__icon--play"
                        />
                    </div>
                    <div class="video-player__controls__volume">
                        <IconRenderer
                            v-if="volume > 0"
                            @click="setVolume(0)"
                            name="VolumeOn"
                            class="video-player__controls__volume__icon video-player__controls__volume__icon--on"
                        />
                        <IconRenderer
                            v-else
                            @click="setVolume(.5)"
                            name="VolumeOff"
                            class="video-player__controls__volume__icon video-player__controls__volume__icon--off"
                        />
                        <div class="video-player__controls__slider video-player__controls__slider--volume">
                            <div
                                :style="{width: `${volume * 100}%`}"
                                class="video-player__controls__slider__complete"
                            ></div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                :value="volume"
                                @input="setVolume($event.target.value)"
                                class="video-player__controls__slider__input"
                            >
                        </div>
                    </div>
                    <div class="video-player__controls__info">
                        <div class="video-player__controls__info__location">
                            {{ formattedLocation }}
                        </div>
                        <div class="video-player__controls__info__duration">
                            {{ formattedDuration }}
                        </div>
                    </div>
                    <IconRenderer
                        @click="fullscreen"
                        name="Expand"
                        class="video-player__controls__fullscreen"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    props: {
        src: null,
        id: null,
        vendor: null,
        resolve: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            started: false,
            playing: false,
            volume: null,
            location: 0,
            duration: 0
        }
    },

    computed: {
        player() {
            switch (this.vendor) {
                case 'vimeo':
                    return {
                        component: 'VideoVimeo',
                        attributes: {
                            id: this.id
                        }
                    }
            }

            return {
                component: 'BaseVideo',
                attributes: {
                    src: this.src,
                    resolve: this.resolve
                }
            };
        },

        classes() {
            const classes = [];

            if (this.started) {
                classes.push('video-player--started');
            }

            if (this.vendor) {
                classes.push(`video-player--${this.vendor}`);
            }

            return classes;
        },

        formattedDuration() {
            return this.timeFormatter(this.duration);
        },

        formattedLocation() {
            return this.timeFormatter(this.location);
        }
    },

    methods: {
        init() {
            this.$refs.player.getVolume().then(volume => {
                this.volume = volume;
            });

            this.$refs.player.getDuration().then(duration => {
                this.duration = duration;
            });

            if (this.vendor === 'vimeo') {
                this.$refs.player.getWidth().then((width) => {
                    this.$refs.container.style.setProperty('--video-width', width);
                });

                this.$refs.player.getHeight().then((height) => {
                    this.$refs.container.style.setProperty('--video-height', height);
                });
            }
        },

        play() {
            this.$refs.player.play();
        },

        pause() {
            this.$refs.player.pause();
        },

        togglePlayState() {
            if (this.playing) {
                this.pause();
                return;
            }

            this.play();
        },

        seek(value) {
            this.location = value;

            this.$refs.player.seek(
                Math.round((this.duration / 100) * value)
            );
        },

        setVolume(value) {
            this.$refs.player.setVolume(value);
        },

        fullscreen() {
            this.$refs.player.fullscreen();
        },

        timeFormatter(seconds) {
            let minutes = Math.floor(seconds / 60);

            return [
                minutes,
                `${Math.floor(seconds - (minutes * 60))}`.padStart(2, '0')
            ].join(':');
        },

        onReady() {
            this.init();
        },

        onPlay() {
            this.started = true;
            this.playing = true;
        },

        onPause() {
            this.playing = false;
        },

        onTimeupdate(e) {
            this.location = e.percent;
        },

        onVolumechange(volume) {
            this.volume = volume;
        },

        onEnd() {
            this.started = false;
        }
    },
}

</script>

<style lang="postcss">
.video-player {
    @apply relative bg-cod-gray overflow-hidden;

    &--started {
        .video-player__controls__play {
            @apply opacity-0 pointer-events-none;
        }

        .video-player__controls__bar {
            transition-delay: 1200ms;
        }

        &:hover {
            .video-player__controls__bar {
                @apply transform-none;
                transition-delay: 0ms;
            }
        }
    }

    &--vimeo {
        .video-player__player {
            @apply relative;
            padding-bottom: calc(var(--video-height) / var(--video-width) * 100%);

            iframe {
                @apply absolute top-0 left-0 w-full h-full;
            }
        }
    }

    &__controls {
        @apply absolute top-0 left-0 flex items-center justify-center w-full h-full z-10;

        &__play {
            @apply flex items-center justify-center w-23 h-23 bg-white bg-opacity-50 rounded-full cursor-pointer;
            transition: opacity 500ms, background-color 400ms;

            &:hover {
                @apply bg-opacity-70;
            }

            &__icon {
                @apply relative left-1 fill-current text-white w-7;
            }
        }

        &__bar {
            @apply absolute bottom-0 left-0 w-full;
            transform: translateY(100%);
            transition: transform 600ms;
        }

        &__slider {
            @apply relative bg-white h-2;

            &--volume {
                @apply h-1 w-20;
            }

            &__complete {
                @apply absolute top-0 left-0 h-full w-10 bg-golden-fizz;
            }

            &__input {
                @apply appearance-none relative z-10 block w-full bg-transparent cursor-pointer;

                &::-webkit-slider-thumb {
                    @apply appearance-none w-0 h-2 bg-golden-fizz;
                }
            }
        }

        &__location {
            @apply overflow-hidden;
        }

        &__dashboard {
            @apply flex items-center p-3 bg-cod-gray bg-opacity-50;

            @screen lg {
                @apply px-5;
            }
        }

        &__play-pause {
            @apply flex items-center justify-center w-8.5 h-8.5 mr-2 rounded-full border-2 border-white border-opacity-50 cursor-pointer;

            &__icon {
                @apply relative fill-current text-white;

                &--play {
                    @apply left-0.5 w-2.5;
                }

                &--pause {
                    @apply w-2;
                }
            }
        }

        &__volume {
            @apply flex items-center justify-between mr-4 pl-3 pr-3.5 py-2 space-x-3 rounded-full border-2 border-white border-opacity-50;

            &__icon {
                @apply fill-current text-white w-5 cursor-pointer;
            }
        }

        &__info {
            @apply hidden;

            @screen lg {
                @apply flex text-xs text-white space-x-2;
                line-height: 11px;
            }

            &__location {
                @apply pr-2 border-r-2 border-white border-opacity-50;
            }
        }

        &__fullscreen {
            @apply ml-auto w-5 fill-current text-white cursor-pointer;
        }
    }
}
</style>
